"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChainAdapter = void 0;
const contracts_1 = require("@ethersproject/contracts");
const caip_1 = require("@shapeshiftoss/caip");
const hdwallet_core_1 = require("@shapeshiftoss/hdwallet-core");
const types_1 = require("@shapeshiftoss/types");
const axios_1 = __importDefault(require("axios"));
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const multicoin_address_validator_1 = __importDefault(require("multicoin-address-validator"));
const web3_utils_1 = require("web3-utils");
const ErrorHandler_1 = require("../error/ErrorHandler");
const utils_1 = require("../utils");
const bignumber_1 = require("../utils/bignumber");
const erc20Abi_json_1 = __importDefault(require("./erc20Abi.json"));
async function getErc20Data(to, value, contractAddress) {
    if (!contractAddress)
        return '';
    const erc20Contract = new contracts_1.Contract(contractAddress, erc20Abi_json_1.default);
    const { data: callData } = await erc20Contract.populateTransaction.transfer(to, value);
    return callData || '';
}
class ChainAdapter {
    constructor(args) {
        this.chainId = 'eip155:1';
        if (args.chainId) {
            try {
                const { chain } = (0, caip_1.fromChainId)(args.chainId);
                if (chain !== types_1.ChainTypes.Ethereum) {
                    throw new Error();
                }
                this.chainId = args.chainId;
            }
            catch (e) {
                throw new Error(`The ChainID ${args.chainId} is not supported`);
            }
        }
        this.providers = args.providers;
    }
    getType() {
        return types_1.ChainTypes.Ethereum;
    }
    getChainId() {
        return this.chainId;
    }
    getFeeAssetId() {
        return 'eip155:1/slip44:60';
    }
    async getAccount(pubkey) {
        try {
            const chainId = this.getChainId();
            const { chain, network } = (0, caip_1.fromChainId)(chainId);
            const { data } = await this.providers.http.getAccount({ pubkey });
            const balance = (0, bignumber_1.bnOrZero)(data.balance).plus((0, bignumber_1.bnOrZero)(data.unconfirmedBalance));
            return {
                balance: balance.toString(),
                chainId,
                assetId: (0, caip_1.toAssetId)({
                    chain,
                    network,
                    assetNamespace: 'slip44',
                    assetReference: caip_1.ASSET_REFERENCE.Ethereum
                }),
                chain: types_1.ChainTypes.Ethereum,
                chainSpecific: {
                    nonce: data.nonce,
                    tokens: data.tokens.map((token) => ({
                        balance: token.balance,
                        assetId: (0, caip_1.toAssetId)({
                            chain,
                            network,
                            assetNamespace: (0, utils_1.getAssetNamespace)(token.type),
                            assetReference: token.contract
                        })
                    }))
                },
                pubkey: data.pubkey
            };
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    buildBIP44Params(params) {
        return { ...ChainAdapter.defaultBIP44Params, ...params };
    }
    // @ts-ignore: keep type signature with unimplemented state
    async getTxHistory({ 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pubkey }) {
        throw new Error('Method not implemented.');
    }
    async buildSendTransaction(tx) {
        var _a, _b, _c;
        try {
            const { to, wallet, bip44Params = ChainAdapter.defaultBIP44Params, chainSpecific: { erc20ContractAddress, gasPrice, gasLimit, maxFeePerGas, maxPriorityFeePerGas }, sendMax = false } = tx;
            if (!to)
                throw new Error('EthereumChainAdapter: to is required');
            if (!(tx === null || tx === void 0 ? void 0 : tx.value))
                throw new Error('EthereumChainAdapter: value is required');
            const destAddress = erc20ContractAddress !== null && erc20ContractAddress !== void 0 ? erc20ContractAddress : to;
            const path = (0, utils_1.toPath)(bip44Params);
            const addressNList = (0, hdwallet_core_1.bip32ToAddressNList)(path);
            const from = await this.getAddress({ bip44Params, wallet });
            const { chainSpecific } = await this.getAccount(from);
            const isErc20Send = !!erc20ContractAddress;
            if (sendMax) {
                const account = await this.getAccount(from);
                if (isErc20Send) {
                    if (!erc20ContractAddress)
                        throw new Error('no token address');
                    const erc20Balance = (_c = (_b = (_a = account === null || account === void 0 ? void 0 : account.chainSpecific) === null || _a === void 0 ? void 0 : _a.tokens) === null || _b === void 0 ? void 0 : _b.find((token) => {
                        return (0, caip_1.fromAssetId)(token.assetId).assetReference === erc20ContractAddress.toLowerCase();
                    })) === null || _c === void 0 ? void 0 : _c.balance;
                    if (!erc20Balance)
                        throw new Error('no balance');
                    tx.value = erc20Balance;
                }
                else {
                    if ((0, bignumber_1.bnOrZero)(account.balance).isZero())
                        throw new Error('no balance');
                    // (The type system guarantees that either maxFeePerGas or gasPrice will be undefined, but not both)
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const fee = (0, bignumber_1.bnOrZero)((maxFeePerGas !== null && maxFeePerGas !== void 0 ? maxFeePerGas : gasPrice)).times((0, bignumber_1.bnOrZero)(gasLimit));
                    tx.value = (0, bignumber_1.bnOrZero)(account.balance).minus(fee).toString();
                }
            }
            const data = await getErc20Data(to, tx === null || tx === void 0 ? void 0 : tx.value, erc20ContractAddress);
            const txToSign = {
                addressNList,
                value: (0, web3_utils_1.numberToHex)(isErc20Send ? '0' : tx === null || tx === void 0 ? void 0 : tx.value),
                to: destAddress,
                chainId: 1,
                data,
                nonce: (0, web3_utils_1.numberToHex)(chainSpecific.nonce),
                gasLimit: (0, web3_utils_1.numberToHex)(gasLimit),
                ...(gasPrice !== undefined
                    ? {
                        gasPrice: (0, web3_utils_1.numberToHex)(gasPrice)
                    }
                    : {
                        // (The type system guarantees that on this branch both of these will be set)
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        maxFeePerGas: (0, web3_utils_1.numberToHex)(maxFeePerGas),
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        maxPriorityFeePerGas: (0, web3_utils_1.numberToHex)(maxPriorityFeePerGas)
                    })
            };
            return { txToSign };
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async signTransaction(signTxInput) {
        try {
            const { txToSign, wallet } = signTxInput;
            const signedTx = await wallet.ethSignTx(txToSign);
            if (!signedTx)
                throw new Error('Error signing tx');
            return signedTx.serialized;
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async signAndBroadcastTransaction(signTxInput) {
        var _a;
        try {
            const { txToSign, wallet } = signTxInput;
            const ethHash = await ((_a = wallet === null || wallet === void 0 ? void 0 : wallet.ethSendTx) === null || _a === void 0 ? void 0 : _a.call(wallet, txToSign));
            if (!ethHash)
                throw new Error('Error signing & broadcasting tx');
            return ethHash.hash;
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async broadcastTransaction(hex) {
        const { data } = await this.providers.http.sendTx({ sendTxBody: { hex } });
        return data;
    }
    async getFeeData({ to, value, chainSpecific: { contractAddress, from, contractData }, sendMax = false }) {
        var _a, _b, _c;
        const { data: responseData } = await axios_1.default.get('https://gas.api.0x.org/');
        const fees = responseData.result.find((result) => result.source === 'MEDIAN');
        if (!fees)
            throw new TypeError('ETH Gas Fees should always exist');
        const isErc20Send = !!contractAddress;
        // Only care about sendMax for erc20
        // its hard to estimate eth fees for sendmax to contracts
        // in MOST cases any eth amount will cost the same 21000 gas
        if (sendMax && isErc20Send && contractAddress) {
            const account = await this.getAccount(from);
            const erc20Balance = (_c = (_b = (_a = account === null || account === void 0 ? void 0 : account.chainSpecific) === null || _a === void 0 ? void 0 : _a.tokens) === null || _b === void 0 ? void 0 : _b.find((token) => {
                const { assetReference } = (0, caip_1.fromAssetId)(token.assetId);
                return assetReference === contractAddress.toLowerCase();
            })) === null || _c === void 0 ? void 0 : _c.balance;
            if (!erc20Balance)
                throw new Error('no balance');
            value = erc20Balance;
        }
        const data = contractData !== null && contractData !== void 0 ? contractData : (await getErc20Data(to, value, contractAddress));
        const { data: gasLimit } = await this.providers.http.estimateGas({
            from,
            to: isErc20Send ? contractAddress : to,
            value: isErc20Send ? '0' : value,
            data
        });
        const feeData = (await this.providers.http.getGasFees()).data;
        const normalizationConstants = {
            fast: (0, bignumber_1.bnOrZero)((0, bignumber_1.bn)(fees.fast).dividedBy(fees.standard)),
            average: (0, bignumber_1.bn)(1),
            slow: (0, bignumber_1.bnOrZero)((0, bignumber_1.bn)(fees.low).dividedBy(fees.standard))
        };
        return {
            fast: {
                txFee: (0, bignumber_1.bnOrZero)((0, bignumber_1.bn)(fees.fast).times(gasLimit)).toPrecision(),
                chainSpecific: {
                    gasLimit,
                    gasPrice: (0, bignumber_1.bnOrZero)(fees.fast).toString(),
                    maxFeePerGas: (0, bignumber_1.bnOrZero)(feeData.maxFeePerGas)
                        .times(normalizationConstants.fast)
                        .toFixed(0, bignumber_js_1.default.ROUND_CEIL)
                        .toString(),
                    maxPriorityFeePerGas: (0, bignumber_1.bnOrZero)(feeData.maxPriorityFeePerGas)
                        .times(normalizationConstants.fast)
                        .toFixed(0, bignumber_js_1.default.ROUND_CEIL)
                        .toString()
                }
            },
            average: {
                txFee: (0, bignumber_1.bnOrZero)((0, bignumber_1.bn)(fees.standard).times(gasLimit)).toPrecision(),
                chainSpecific: {
                    gasLimit,
                    gasPrice: (0, bignumber_1.bnOrZero)(fees.standard).toString(),
                    maxFeePerGas: (0, bignumber_1.bnOrZero)(feeData.maxFeePerGas)
                        .times(normalizationConstants.average)
                        .toFixed(0, bignumber_js_1.default.ROUND_CEIL)
                        .toString(),
                    maxPriorityFeePerGas: (0, bignumber_1.bnOrZero)(feeData.maxPriorityFeePerGas)
                        .times(normalizationConstants.average)
                        .toFixed(0, bignumber_js_1.default.ROUND_CEIL)
                        .toString()
                }
            },
            slow: {
                txFee: (0, bignumber_1.bnOrZero)((0, bignumber_1.bn)(fees.low).times(gasLimit)).toPrecision(),
                chainSpecific: {
                    gasLimit,
                    gasPrice: (0, bignumber_1.bnOrZero)(fees.low).toString(),
                    maxFeePerGas: (0, bignumber_1.bnOrZero)(feeData.maxFeePerGas)
                        .times(normalizationConstants.slow)
                        .toFixed(0, bignumber_js_1.default.ROUND_CEIL)
                        .toString(),
                    maxPriorityFeePerGas: (0, bignumber_1.bnOrZero)(feeData.maxPriorityFeePerGas)
                        .times(normalizationConstants.slow)
                        .toFixed(0, bignumber_js_1.default.ROUND_CEIL)
                        .toString()
                }
            }
        };
    }
    async getAddress(input) {
        const { wallet, bip44Params = ChainAdapter.defaultBIP44Params } = input;
        const path = (0, utils_1.toPath)(bip44Params);
        const addressNList = (0, hdwallet_core_1.bip32ToAddressNList)(path);
        const ethAddress = await wallet.ethGetAddress({
            addressNList,
            showDisplay: Boolean(input.showOnDevice)
        });
        return ethAddress;
    }
    async validateAddress(address) {
        const isValidAddress = multicoin_address_validator_1.default.validate(address, this.getType());
        if (isValidAddress)
            return { valid: true, result: types_1.chainAdapters.ValidAddressResultType.Valid };
        return { valid: false, result: types_1.chainAdapters.ValidAddressResultType.Invalid };
    }
    async validateEnsAddress(address) {
        const isValidEnsAddress = /^([0-9A-Z]([-0-9A-Z]*[0-9A-Z])?\.)+eth$/i.test(address);
        if (isValidEnsAddress)
            return { valid: true, result: types_1.chainAdapters.ValidAddressResultType.Valid };
        return { valid: false, result: types_1.chainAdapters.ValidAddressResultType.Invalid };
    }
    async subscribeTxs(input, onMessage, onError) {
        const { wallet, bip44Params = ChainAdapter.defaultBIP44Params } = input;
        const address = await this.getAddress({ wallet, bip44Params });
        const subscriptionId = (0, utils_1.toRootDerivationPath)(bip44Params);
        await this.providers.ws.subscribeTxs(subscriptionId, { topic: 'txs', addresses: [address] }, ({ data: tx }) => {
            var _a;
            const transfers = tx.transfers.map((transfer) => ({
                assetId: transfer.assetId,
                from: transfer.from,
                to: transfer.to,
                type: (0, utils_1.getType)(transfer.type),
                value: transfer.totalValue
            }));
            onMessage({
                address: tx.address,
                blockHash: tx.blockHash,
                blockHeight: tx.blockHeight,
                blockTime: tx.blockTime,
                chainId: tx.chainId,
                chain: types_1.ChainTypes.Ethereum,
                confirmations: tx.confirmations,
                fee: tx.fee,
                status: (0, utils_1.getStatus)(tx.status),
                tradeDetails: tx.trade,
                transfers,
                txid: tx.txid,
                ...(tx.data && {
                    data: {
                        method: tx.data.method,
                        parser: (_a = tx.data.parser) !== null && _a !== void 0 ? _a : 'unknown'
                    }
                })
            });
        }, (err) => onError({ message: err.message }));
    }
    unsubscribeTxs(input) {
        if (!input)
            return this.providers.ws.unsubscribeTxs();
        const { bip44Params = ChainAdapter.defaultBIP44Params } = input;
        const subscriptionId = (0, utils_1.toRootDerivationPath)(bip44Params);
        this.providers.ws.unsubscribeTxs(subscriptionId, { topic: 'txs', addresses: [] });
    }
    closeTxs() {
        this.providers.ws.close('txs');
    }
}
exports.ChainAdapter = ChainAdapter;
ChainAdapter.defaultBIP44Params = {
    purpose: 44,
    coinType: 60,
    accountNumber: 0
};
